import { useCallback, useRef } from 'react';

import { Skeleton, Table } from '@radix-ui/themes';
import { Table as TanstackTable, flexRender } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import cn from 'classnames';
import { t } from 'i18next';

import { FlyEmptyState } from '../../FlyEmptyState/FlyEmptyState';
import { TANSTACK_TABLE_VIRTUAL_SCROLL_VIEWPORT_TEST_ID } from '../TanstackTable.types';
import { getColumnMeta } from '../utils/getColumnHtmlMeta';
import { SkeletonCell } from './SkeletonCell';
import { TanstackTableHeader } from './TanstackTableHeader';

export interface TanstackTableVirtualViewProps<T extends Record<string, any>> {
  table: TanstackTable<T>;
  isFilterDataEmpty: boolean;
  isDataEmpty: boolean;
  loading: boolean;
  loadingMore?: boolean;
  skeletonRows: number;
  emptyStateElement: React.ReactNode;
  layout: 'fixed' | 'auto';
  /** Estimated row height in px. Default 40. */
  rowHeight?: number;
  /** Number of skeleton rows to show at bottom while loading next page. Default 5. */
  loadMoreSkeletonRows?: number;
  /** Cursor to pass to onLoadMore when user scrolls near end (e.g. pageInfo.endCursor when hasNextPage). */
  nextCursor: string | null | undefined;
  /** Called when user scrolls near the end. Parent should fetch with after=cursor and append. Only called when nextCursor is set and not already loading. */
  onLoadMore: (cursor: string) => void;
  /** `data-testid` on the overflow scroll container (virtualizer root). */
  scrollViewportTestId?: string;
  /** Optional `data-testid` on filter-empty FlyEmptyState (automation parity with custom renders). */
  filterEmptyStateTestId?: string;
}

const LOAD_MORE_THRESHOLD_PX = 400;

export function TanstackTableVirtualView<T extends Record<string, any>>({
  table,
  isFilterDataEmpty,
  isDataEmpty,
  loading,
  loadingMore = false,
  skeletonRows,
  emptyStateElement,
  layout,
  rowHeight = 66,
  loadMoreSkeletonRows = 5,
  nextCursor,
  onLoadMore,
  scrollViewportTestId = TANSTACK_TABLE_VIRTUAL_SCROLL_VIEWPORT_TEST_ID,
  filterEmptyStateTestId,
}: TanstackTableVirtualViewProps<T>) {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rows = table.getRowModel().rows;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => rowHeight,
    indexAttribute: 'data-virtual-index',
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const handleScroll = useCallback(() => {
    if (!nextCursor || loading || loadingMore) return;
    const el = tableContainerRef.current;
    if (!el) return;
    const { scrollHeight, scrollTop, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX) {
      onLoadMore(nextCursor);
    }
  }, [nextCursor, loading, loadingMore, onLoadMore]);

  let tableBody: React.ReactNode = null;

  if (loading) {
    tableBody = Array.from({ length: skeletonRows }).map((_, index) => (
      <Table.Row key={index}>
        {table.getAllColumns().map((column) => {
          const { skeletonConfig, htmlProps } = getColumnMeta(
            column.columnDef.meta
          );
          return (
            <Table.Cell
              key={column.id}
              {...htmlProps}
              className={cn(
                (htmlProps as React.HTMLAttributes<HTMLElement>)?.className,
                '!p-2'
              )}
            >
              {skeletonConfig ? (
                <SkeletonCell config={skeletonConfig} />
              ) : (
                <Skeleton width='100%' height='100%' />
              )}
            </Table.Cell>
          );
        })}
      </Table.Row>
    ));
  } else if (rows.length === 0) {
    if (isFilterDataEmpty) {
      tableBody = (
        <Table.Row>
          <Table.Cell colSpan={table.getAllColumns().length}>
            <FlyEmptyState
              {...(filterEmptyStateTestId
                ? { 'data-testid': filterEmptyStateTestId }
                : {})}
              title={t(
                'app.dashboard.runtime_environments.environments.no_data_found_filter.title',
                { defaultValue: 'No Results' }
              )}
              description={t(
                'app.dashboard.runtime_environments.environments.no_data_found_filter.subtitle',
                { defaultValue: 'Remove or adjust your search filters' }
              )}
            />
          </Table.Cell>
        </Table.Row>
      );
    } else if (isDataEmpty) {
      tableBody = (
        <Table.Row>
          <Table.Cell colSpan={table.getAllColumns().length}>
            {emptyStateElement ?? <FlyEmptyState title='No data found' />}
          </Table.Cell>
        </Table.Row>
      );
    }
  } else {
    const totalSize = rowVirtualizer.getTotalSize();
    const skeletonRowHeight = rowHeight;
    const skeletonAreaHeight = loadingMore
      ? loadMoreSkeletonRows * skeletonRowHeight
      : 0;
    const bodyHeight = totalSize + skeletonAreaHeight;
    const columnCount = table.getAllColumns().length;
    tableBody = (
      <>
        {/* Spacer row so tbody has full list height and scrollbar represents all rows (incl. load-more skeleton area) */}
        <Table.Row aria-hidden>
          <Table.Cell
            colSpan={columnCount}
            style={{
              height: `${bodyHeight}px`,
              padding: 0,
              border: 0,
              lineHeight: 0,
              visibility: 'hidden',
              verticalAlign: 'top',
            }}
          />
        </Table.Row>
        {virtualItems.map((virtualRow) => {
          const row = rows[virtualRow.index];
          return (
            <Table.Row
              key={row.id}
              ref={rowVirtualizer.measureElement}
              data-virtual-index={virtualRow.index}
              data-index={virtualRow.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                transform: `translateY(${virtualRow.start}px)`,
                width: '100%',
              }}
            >
              {row.getVisibleCells().map((cell) => {
                const { htmlProps } = getColumnMeta(cell.column.columnDef.meta);
                return (
                  <Table.Cell
                    key={cell.id}
                    {...htmlProps}
                    className={cn(
                      (htmlProps as React.HTMLAttributes<HTMLElement>)
                        ?.className,
                      'h-full'
                    )}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      boxSizing: 'border-box',
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          );
        })}
        {loadingMore &&
          Array.from({ length: loadMoreSkeletonRows }).map((_, index) => (
            <Table.Row
              key={`load-more-skeleton-${index}`}
              style={{
                position: 'absolute',
                top: totalSize + index * skeletonRowHeight,
                left: 0,
                width: '100%',
                height: `${skeletonRowHeight}px`,
              }}
            >
              {table.getAllColumns().map((column) => {
                const { skeletonConfig, htmlProps } = getColumnMeta(
                  column.columnDef.meta
                );
                return (
                  <Table.Cell
                    key={column.id}
                    {...htmlProps}
                    className={cn(
                      (htmlProps as React.HTMLAttributes<HTMLElement>)
                        ?.className,
                      '!p-2 flex h-full items-center justify-center'
                    )}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      boxSizing: 'border-box',
                    }}
                  >
                    {skeletonConfig ? (
                      <SkeletonCell config={skeletonConfig} />
                    ) : (
                      <div className='h-full !max-h-[40px] w-full'>
                        <Skeleton width='100%' height='100%' />
                      </div>
                    )}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          ))}
      </>
    );
  }

  const hasVirtualRows = !loading && rows.length > 0;

  if (hasVirtualRows) {
    const totalSize = rowVirtualizer.getTotalSize();
    const skeletonAreaHeight = loadingMore
      ? loadMoreSkeletonRows * rowHeight
      : 0;
    const bodyHeight = totalSize + skeletonAreaHeight;
    return (
      <div className='min-h-0 flex flex-1 flex-col'>
        <div
          ref={tableContainerRef}
          data-testid={scrollViewportTestId}
          onScroll={handleScroll}
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--gray-a8) transparent',
          }}
          className='w-full'
        >
          <Table.Root
            variant='surface'
            className='min-h-0 w-full bg-transparent'
            layout={layout}
          >
            <TanstackTableHeader table={table} interactive />
            <Table.Body
              className='bg-transparent'
              style={{
                display: 'block',
                position: 'relative',
                height: `${bodyHeight}px`,
              }}
            >
              {tableBody}
            </Table.Body>
          </Table.Root>
        </div>
      </div>
    );
  }

  return (
    <Table.Root
      variant='surface'
      className='min-h-0 bg-transparent'
      layout={layout}
    >
      <TanstackTableHeader table={table} interactive={!loading} />
      <Table.Body className='bg-transparent'>{tableBody}</Table.Body>
    </Table.Root>
  );
}
